import { AnyAction } from 'redux';
import { TopResultData } from '../../../../../web/models/selfHelp';
import * as actionTypes from '../../../../models/actionTypes';
import { AlchemyInsightModel, BcqrExperimentType } from '../../../../models/apiModels';
import { OutdatedDevicesAlertEnabledComponent, VsbUpsellAlertEnabledComponent } from '../../../../models/constants/constants';
import { ISelfHelpAlchemyState, initialSelfHelpAlchemyState } from '../../../../models/reduxState';
import { deepCopyProperty, isAlchemyEnabledForEnvironmet, isAlchemyEnabledForEnvironmetAndAppVer } from '../../../../utilities';

const handleSocDataGrabSuccess = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction) => {
  return {
    ...state,
    isAlchmeySolutionsEnabled: !!action.supportSettingsConfig ? isAlchemyEnabledForEnvironmet(action.environment, action.supportSettingsConfig.AlchemySolutionEnvironmentsEnabled) : false,
    isAlchmeyInsightsEnabled: !!action.supportSettingsConfig ? isAlchemyEnabledForEnvironmetAndAppVer(action.environment, action.appVer, action.supportSettingsConfig.AlchemyInsightEnvironmentsEnabled, action.supportSettingsConfig.AlchemyInsightAppVersEnabled) : false,
    isMultiInsightsEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsMultiInsightsEnabled,
    isInsightActionButtonEnabled: !!action.supportSettingsConfig ? isAlchemyEnabledForEnvironmet(action.environment, action.supportSettingsConfig.InsightActionButtonEnvironmentsEnabled) : false,
    isJumpToFirstLinkEnabled: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.JumpToFirstLinkEnabled : true,
    disambiguationBranchingEnabled: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.DisambiguationBranchingEnabled : false,
    showModalitiesStickyFooter: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.ShowModalitiesStickyFooter : false,
    showRecommendedArticlesHeader: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.ShowRecommendedArticlesHeader : false,
    showAutoSuggestCard: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.ShowAutoSuggestCard : false,
    bcqrExperimentType: !!action.supportSettingsConfig ? action.supportSettingsConfig.BcqrExperimentType : BcqrExperimentType.Control,
    isTopResultForSelfHelpEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsTopResultForSelfHelpEnabled,
    hideGetHelpBtn: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsGetHelpBtnHidden,
    hidePrivacyAndSupport: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsPrivacyAndSupportHidden,
    showBCQRV2: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShowBCQRV2,
  };
};

const handleSupportDataGrabSuccess = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction) => {
  return {
    ...state,
    isAlchmeySolutionsEnabled: !!action.supportSettingsConfig ? isAlchemyEnabledForEnvironmet(action.environment, action.supportSettingsConfig.AlchemySolutionEnvironmentsEnabled) : false,
    isAlchmeyInsightsEnabled: !!action.supportSettingsConfig ? isAlchemyEnabledForEnvironmetAndAppVer(action.environment, action.appVer, action.supportSettingsConfig.AlchemyInsightEnvironmentsEnabled, action.supportSettingsConfig.AlchemyInsightAppVersEnabled) : false,
    isMultiInsightsEnabled: !!!action.supportSettingsConfig && !!action.supportSettingsConfig.IsMultiInsightsEnabled,
    isInsightActionButtonEnabled: !!action.supportSettingsConfig ? isAlchemyEnabledForEnvironmet(action.environment, action.supportSettingsConfig.InsightActionButtonEnvironmentsEnabled) : false,
    isJumpToFirstLinkEnabled: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.JumpToFirstLinkEnabled : true,
    disambiguationBranchingEnabled: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.DisambiguationBranchingEnabled : false,
    showModalitiesStickyFooter: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.ShowModalitiesStickyFooter : false,
    showRecommendedArticlesHeader: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.ShowRecommendedArticlesHeader : false,
    showAutoSuggestCard: !!action.supportSettingsConfig ? !!action.supportSettingsConfig.ShowAutoSuggestCard : false,
    bcqrExperimentType: !!action.supportSettingsConfig ? action.supportSettingsConfig.BcqrExperimentType : BcqrExperimentType.Control,
    isTopResultForSelfHelpEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsTopResultForSelfHelpEnabled,
    hideGetHelpBtn: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsGetHelpBtnHidden,
    hidePrivacyAndSupport: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsPrivacyAndSupportHidden,
    showBCQRV2: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShowBCQRV2,
  };
};

const handleInitializeAlchemyStateOnSessionStart = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, _action: AnyAction): ISelfHelpAlchemyState => {
  return {
    ...state,
    alchemyResults: null,
    isAlchemyQueryInProgress: true,
    areSolutionsPresent: false,
    areInsightsPresent: false,
    areAIInsightsPresent: false,
    insightFeedbackSubmited: false,
  };
};

const handleAlchemySuccess = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction) => {
  if (!!action?.alchemyResults?.ThemeRoutingData?.Nodes) {
    action.alchemyResults.ThemeRoutingData.NodesMap = {};
    for (let i = 0; i < action.alchemyResults.ThemeRoutingData.Nodes.length; i++) {
      const node = action.alchemyResults.ThemeRoutingData.Nodes[i];
      action.alchemyResults.ThemeRoutingData.NodesMap[node.Id] = node;
    }
  }

  if (!!action.alchemyResults && !!action.alchemyResults.Insights && action.alchemyResults.Insights.length > 0) {
    action.alchemyResults.Insights[0].IsExpanded = true;
  }

  return {
    ...state,
    alchemyResults: !!action.alchemyResults ? action.alchemyResults : {},
    isAlchemyQueryInProgress: false,
    isAlchemyQueryCompleted: true,
    areSolutionsPresent: !!action.alchemyResults && !!action.alchemyResults.Solutions && action.alchemyResults.Solutions.length > 0,
    areInsightsPresent: !!action.alchemyResults && !!action.alchemyResults.Insights && action.alchemyResults.Insights.length > 0,
    areAIInsightsPresent: !!action.alchemyResults && !!action.alchemyResults.AIInsights,
  };
};

const handleAlchemyFailure = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction) => {
  if (!!action?.alchemyResults?.ThemeRoutingData?.Nodes) {
    action.alchemyResults.ThemeRoutingData.NodesMap = {};
    for (let i = 0; i < action.alchemyResults.ThemeRoutingData.Nodes.length; i++) {
      const node = action.alchemyResults.ThemeRoutingData.Nodes[i];
      action.alchemyResults.ThemeRoutingData.NodesMap[node.Id] = node;
    }
  }

  return {
    ...state,
    alchemyResults: action.alchemyResults,
    isAlchemyQueryInProgress: false,
    isAlchemyQueryCompleted: true,
    areSolutionsPresent: false,
    areInsightsPresent: false,
    areAIInsightsPresent: false,
  };
};

const handleAlchemyInsightFeedbackSubmited = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction) => {
  return {
    ...state,
    insightFeedbackSubmited: true,
  };
};

const handleSetRelatedCorrelationIds = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState) => {
  const correlationId = state.alchemyResults ? state.alchemyResults.CorrelationId : '';
  let relatedSHCorrelationIds = correlationId;
  if (correlationId && state.relatedSHCorrelationIds && !(state.relatedSHCorrelationIds.indexOf(correlationId) !== -1)) {
    relatedSHCorrelationIds = `${state.relatedSHCorrelationIds};${correlationId}`;
  }
  return {
    ...state,
    relatedSHCorrelationIds: relatedSHCorrelationIds,
  };
};

const handleClearingRelatedCorrelationIds = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState) => {
  return {
    ...state,
    relatedSHCorrelationIds: '',
  };
};

const handleDismissOutdatedDevicesAlert = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  if (action.callingComponent === OutdatedDevicesAlertEnabledComponent.SelfHelpAlchemy) {
    return {
      ...state,
      isOutdatedDevicesAlertDismissed: true,
    };
  }
  return { ...state };
};

const handleWindowsUpdateAvailableForSA = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
): ISelfHelpAlchemyState => {
  return {
    ...state,
    isWindowsUpdateAvailableForSA: true,
  };
};

const handleDismissWindowsUpdateAvailableForSA = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
): ISelfHelpAlchemyState => {
  return {
    ...state,
    isWindowsUpdateAvailableForSA: false,
  };
};

const handleEndOfLifeMessageForSA = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
): ISelfHelpAlchemyState => {
  return {
    ...state,
    isEndOfLifeReachedForSA: true,
  };
};

const handleDismissEndOfLifeMessageForSA = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
): ISelfHelpAlchemyState => {
  return {
    ...state,
    isEndOfLifeReachedForSA: false,
  };
};

const handleDismissVsbUpsellAlert = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  if (action.callingComponent === VsbUpsellAlertEnabledComponent.SelfHelpAlchemy) {
    return {
      ...state,
      isVsbUpsellAlertDismissed: true,
    };
  }
  return { ...state };
};

const handleCTAClickedVsbUpsellAlert = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  return {
    ...state,
    isVsbUpsellAlertCTAClicked: true,
  };
};

const handleVsbUpsellAlertActivated = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
): ISelfHelpAlchemyState => {
  return {
    ...state,
    isVsbUpsellAlertActivated: true,
  };
};

const handleToggleOutdatedDevicesAlertsExpand = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  if (action.callingComponent === OutdatedDevicesAlertEnabledComponent.SelfHelpAlchemy) {
    return {
      ...state,
      shouldExpandOutdatedDevicesAlert: !state.shouldExpandOutdatedDevicesAlert,
    };
  }
  return { ...state };
};

const handleSetExpandedInsight = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  const newResults = deepCopyProperty(state.alchemyResults);
  newResults.Insights.map((insight: AlchemyInsightModel) => insight.IsExpanded = false);
  newResults.Insights[action.insightIndex].IsExpanded = !action.isExpanded;
  return {
    ...state,
    alchemyResults: newResults,
  };
};

/**
 * Whenever you define a new property to ISelfHelpAlchemyState, consider the need to reset it if it's impacted by search or ticket creation
 */
const handleSearchSessionReset = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState) => {
  return {
    ...state,
    relatedSHCorrelationIds: initialSelfHelpAlchemyState.relatedSHCorrelationIds,
    alchemyResults: initialSelfHelpAlchemyState.alchemyResults,
    isAlchemyQueryInProgress: initialSelfHelpAlchemyState.isAlchemyQueryInProgress,
    isAlchemyQueryCompleted: initialSelfHelpAlchemyState.isAlchemyQueryCompleted,
    areSolutionsPresent: initialSelfHelpAlchemyState.areSolutionsPresent,
    areInsightsPresent: initialSelfHelpAlchemyState.areInsightsPresent,
    areAIInsightsPresent: initialSelfHelpAlchemyState.areAIInsightsPresent,
    insightFeedbackSubmited: initialSelfHelpAlchemyState.insightFeedbackSubmited,
    shouldExpandDeviceList: initialSelfHelpAlchemyState.shouldExpandOutdatedDevicesAlert,
    dialogId: initialSelfHelpAlchemyState.dialogId,
  };
};

const handleTopResultHeight = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  if (!state.alchemyResults.TopResults) {
    return state;
  }
  const topResults: TopResultData[] = [...state.alchemyResults.TopResults];
  const index = action.payload;
  topResults[index] = { ...topResults[index], expanded: !topResults[index].expanded };
  return {
    ...state,
    alchemyResults: { ...state.alchemyResults, TopResults: topResults },
  };
};

const handleTopResultContent = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
  action: AnyAction
): ISelfHelpAlchemyState => {
  if (!state.alchemyResults.TopResults) {
    return state;
  }
  const topResults: TopResultData[] = [...state.alchemyResults.TopResults];
  const index = action.payload;
  topResults[index] = { ...topResults[index], hideContent: !topResults[index].hideContent };
  return {
    ...state,
    alchemyResults: { ...state.alchemyResults, TopResults: topResults },
  };
};

const handleUpdateDialogId = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction): ISelfHelpAlchemyState => {
  return {
    ...state,
    dialogId: action.dialogId,
  };
};

const handleToggleShowMoreInsights = (
  state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState,
): ISelfHelpAlchemyState => {
    return {
      ...state,
      showMoreInsightsExpanded: !state.showMoreInsightsExpanded,
    };
};

const handleSetQueryChanged = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction): ISelfHelpAlchemyState => {
  return {
    ...state,
    isQueryChanged: action.isQueryChanged,
  }
}

export const SelfHelpAlchemyReducer = (state: ISelfHelpAlchemyState = initialSelfHelpAlchemyState, action: AnyAction) => {
  switch (action.type) {
    case actionTypes.INIT_SELF_HELP_ALCHEMY_ON_SESSION_START:
      return handleInitializeAlchemyStateOnSessionStart(state, action);
    case actionTypes.FETCH_ALCHEMY_SUCCESS_3P:
    case actionTypes.FETCH_ALCHEMY_SUCCESS:
      return handleAlchemySuccess(state, action);
    case actionTypes.FETCH_ALCHEMY_ERROR:
      return handleAlchemyFailure(state, action);
    case actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS:
      return handleSupportDataGrabSuccess(state, action);
    case actionTypes.GET_EMERALD_CONFIG_SUCCESS:
      return handleSocDataGrabSuccess(state, action);
    case actionTypes.SUBMIT_ALCHEMY_INSIGHT_FEEDBACK:
      return handleAlchemyInsightFeedbackSubmited(state, action);
    case actionTypes.SET_EXPANDED_INSIGHT:
      return handleSetExpandedInsight(state, action);
    case actionTypes.SET_RELATED_CORRELATION_IDS:
      return handleSetRelatedCorrelationIds(state);
    case actionTypes.CLEAR_RELATED_CORRELATION_IDS:
      return handleClearingRelatedCorrelationIds(state);
    case actionTypes.RESET_SEARCH_SESSION:
      return handleSearchSessionReset(state);
    case actionTypes.DISMISS_OUTDATED_DEVICES_ALERT:
      return handleDismissOutdatedDevicesAlert(state, action);
    case actionTypes.DISMISS_VSB_UPSELL_ALERT:
      return handleDismissVsbUpsellAlert(state, action);
    case actionTypes.SET_CTA_CLICKED_VSB_UPSELL_ALERT:
      return handleCTAClickedVsbUpsellAlert(state, action);
    case actionTypes.SET_VSB_UPSELL_ALERT_ACTIVATED:
      return handleVsbUpsellAlertActivated(state);
    case actionTypes.TOGGLE_OUTDATED_DEVICES_ALERT_EXPAND:
      return handleToggleOutdatedDevicesAlertsExpand(state, action);
    case actionTypes.TOGGLE_CARD_HEIGHT:
      return handleTopResultHeight(state, action);
    case actionTypes.TOGGLE_CONTENT:
      return handleTopResultContent(state, action);
    case actionTypes.UPDATE_DIALOG_ID:
      return handleUpdateDialogId(state, action);
    case actionTypes.TOGGLE_SEE_MORE_INSIGHTS:
      return handleToggleShowMoreInsights(state);
    case actionTypes.SET_QUERY_CHANGED:
      return handleSetQueryChanged(state, action);
    case actionTypes.SHOW_WINDOWS_UPDATE_AVAILABLE_FOR_SA:
      return handleWindowsUpdateAvailableForSA(state);
    case actionTypes.DISMISS_WINDOWS_UPDATE_AVAILABLE_FOR_SA:
      return handleDismissWindowsUpdateAvailableForSA(state);
    case actionTypes.SHOW_END_OF_LIFE_MESSAGE_FOR_SA:
      return handleEndOfLifeMessageForSA(state);
    case actionTypes.DISMISS_END_OF_LIFE_MESSAGE_FOR_SA:
      return handleDismissEndOfLifeMessageForSA(state);
    default:
      return state;
  }
};
